import { AxiosResponse } from 'axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import { GraphDateType } from '../domain/dataKiGraph';
import {
  normalizeGraphFieldsForFetch,
  resolveDataModelTransitionGraphFields,
  resolveDataModelTransitionGraphFieldsForSearch,
} from '../domain/dataModelTransitionGraphFields';
import {
  DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_MAX_FIELDS,
  DataTransitionAfterIntroductionGraph,
  DataTransitionAfterIntroductionGraphApiResponse,
  DataTransitionAfterIntroductionGraphFieldItem,
  DataTransitionAfterIntroductionGraphParams,
  DataTransitionAfterIntroductionParams,
  toDataTransitionAfterIntroductionGraphSetting,
} from '../domain/transitionAfterIntroduction/types';

import { dataTransitionAfterIntroductionDataSearchConditionSelector } from '../redux/server/dataTransitionAfterIntroduction';
import {
  ChangeDataTransitionAfterIntroductionKiGraphFieldAction,
  DataTransitionAfterIntroductionKiGraphActionCreators,
  DataTransitionAfterIntroductionKiGraphActionTypes,
  FetchDataTransitionAfterIntroductionKiGraphAction,
  SearchDataTransitionAfterIntroductionKiGraphAction,
  SetDataTransitionAfterIntroductionKiGraphFieldsAction,
  dataTransitionAfterIntroductionKiGraphSettingSelector,
} from '../redux/server/dataTransitionAfterIntroductionKiGraph';
import { TransitionAfterIntroductionReportsSettingActionCreators } from '../redux/ui/transitionAfterIntroductionReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { handleErrorSaga } from './errorSaga';

type GraphFieldFetchSuccess = {
  field: string;
  response: DataTransitionAfterIntroductionGraphApiResponse;
};

type GraphFieldFetchFailure = {
  field: string;
  error: unknown;
};

type GraphFieldFetchResult = GraphFieldFetchSuccess | GraphFieldFetchFailure;

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchSuccess => 'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchFailure => 'error' in result;

const toGraphApiQueryParams = (
  params: DataTransitionAfterIntroductionGraphParams,
  field: string
) => {
  const { field: _field, fields: _fields, ...apiParams } = params;
  return { ...apiParams, field };
};

const buildDataTransitionAfterIntroductionGraph = (
  responses: DataTransitionAfterIntroductionGraphApiResponse[],
  fields: string[]
): DataTransitionAfterIntroductionGraph => {
  const firstSetting = responses.at(0)?.setting;
  if (!firstSetting) {
    throw new Error(
      'Transition after introduction graph API response is empty'
    );
  }

  const graphList: DataTransitionAfterIntroductionGraphFieldItem[] =
    fields.flatMap((field, index) => {
      const response = responses.at(index);
      if (!response) {
        return [];
      }
      return [{ field, data: response.data }];
    });

  return {
    setting: toDataTransitionAfterIntroductionGraphSetting(firstSetting),
    graphList,
  };
};

function* fetchGraphByField(
  api: Api,
  params: DataTransitionAfterIntroductionGraphParams,
  field: string
) {
  const response: AxiosResponse<DataTransitionAfterIntroductionGraphApiResponse> =
    yield call(
      api.get,
      '/data/transitionAfterIntroduction/graph',
      buildConfig(toGraphApiQueryParams(params, field))
    );
  return response.data;
}

function* fetchGraphByFieldSafely(
  api: Api,
  params: DataTransitionAfterIntroductionGraphParams,
  field: string
): Generator<
  unknown,
  GraphFieldFetchResult,
  DataTransitionAfterIntroductionGraphApiResponse
> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    return { field, response };
  } catch (error: unknown) {
    return { field, error };
  }
}

/**
 * 検索条件を元に推移グラフ（機種比較）のデータを取得する
 */
export function* fetchDataTransitionAfterIntroductionKiGraphSaga(
  api: Api,
  action: FetchDataTransitionAfterIntroductionKiGraphAction
) {
  const { params } = action.payload;
  const fields = resolveDataModelTransitionGraphFields(params);

  yield put(
    TransitionAfterIntroductionReportsSettingActionCreators.selectTransitionAfterIntroductionGraphAnimationEnabledAction(
      true
    )
  );

  yield put(
    DataTransitionAfterIntroductionKiGraphActionCreators.fetchDataTransitionAfterIntroductionKiGraphRequestAction()
  );

  const results: GraphFieldFetchResult[] = yield all(
    fields.map((field) => call(fetchGraphByFieldSafely, api, params, field))
  );
  const successfulResults = results.filter(isGraphFieldFetchSuccess);
  const failedResults = results.filter(isGraphFieldFetchFailure);

  if (successfulResults.length === 0) {
    yield put(
      DataTransitionAfterIntroductionKiGraphActionCreators.renewDataTransitionAfterIntroductionKiGraphAction()
    );
    yield put(
      TransitionAfterIntroductionReportsSettingActionCreators.selectTransitionAfterIntroductionGraphAnimationEnabledAction(
        false
      )
    );
    const firstError = failedResults.at(0)?.error;
    if (firstError !== undefined) {
      yield fork(handleErrorSaga, firstError);
    }
    return;
  }

  yield put(
    DataTransitionAfterIntroductionKiGraphActionCreators.fetchDataTransitionAfterIntroductionKiGraphSuccessAction(
      buildDataTransitionAfterIntroductionGraph(
        successfulResults.map((result) => result.response),
        successfulResults.map((result) => result.field)
      )
    )
  );

  if (failedResults.length > 0) {
    console.warn(
      `[dataTransitionAfterIntroductionKiGraphSaga] 一部グラフ項目の取得に失敗しました: ${failedResults
        .map((result) => result.field)
        .join(', ')}`
    );
  }
}

function* refetchDataTransitionAfterIntroductionKiGraphWithFields(
  fields: string[]
) {
  const searchCondition = yield* customSelect(
    dataTransitionAfterIntroductionKiGraphSettingSelector
  );
  if (!searchCondition) {
    console.warn(
      '[dataTransitionAfterIntroductionKiGraphSaga] グラフ検索条件が未設定のため再取得をスキップしました'
    );
    return;
  }

  yield put(
    DataTransitionAfterIntroductionKiGraphActionCreators.fetchDataTransitionAfterIntroductionKiGraphAction(
      {
        ...searchCondition,
        fields: normalizeGraphFieldsForFetch(
          fields,
          DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_MAX_FIELDS
        ),
      }
    )
  );
}

export function* searchDataTransitionAfterIntroductionKiGraphSaga(
  action: SearchDataTransitionAfterIntroductionKiGraphAction
) {
  const { kiList } = action.payload;

  const searchCondition: DataTransitionAfterIntroductionParams | undefined =
    yield* customSelect(
      dataTransitionAfterIntroductionDataSearchConditionSelector
    );

  if (searchCondition === undefined) {
    // グラフボタンはテーブルデータ表示後に出る前提。未取得なら何もできない
    console.warn(
      '[dataTransitionAfterIntroductionKiGraphSaga] テーブル検索条件が未設定のためグラフ取得をスキップしました'
    );
    return;
  }

  const existingGraphParams = yield* customSelect(
    dataTransitionAfterIntroductionKiGraphSettingSelector
  );
  const fields =
    resolveDataModelTransitionGraphFieldsForSearch(existingGraphParams);

  yield put(
    DataTransitionAfterIntroductionKiGraphActionCreators.fetchDataTransitionAfterIntroductionKiGraphAction(
      {
        kiList,
        kiListForSijiritu: searchCondition.kiListForSijiritu,
        shuList: searchCondition.shuList,
        shuGroupIds: searchCondition.shuGroupIds,
        halls: searchCondition.halls,
        dateType: searchCondition.dateType as GraphDateType,
        excludeToday: searchCondition.excludeToday,
        startDate: searchCondition.startDate,
        endDate: searchCondition.endDate,
        areas: searchCondition.areas,
        makers: searchCondition.makers,
        sisTypes: searchCondition.sisTypes,
        fields,
      }
    )
  );
}

function* changeDataTransitionAfterIntroductionKiGraphFieldSaga(
  action: ChangeDataTransitionAfterIntroductionKiGraphFieldAction
) {
  yield* refetchDataTransitionAfterIntroductionKiGraphWithFields([
    action.payload.field,
  ]);
}

function* setDataTransitionAfterIntroductionKiGraphFieldsSaga(
  action: SetDataTransitionAfterIntroductionKiGraphFieldsAction
) {
  yield* refetchDataTransitionAfterIntroductionKiGraphWithFields(
    action.payload.fields
  );
}

function* handleFetchDataTransitionAfterIntroductionKiGraphSaga(api: Api) {
  yield takeLatest(
    DataTransitionAfterIntroductionKiGraphActionTypes.FETCH_DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH,
    fetchDataTransitionAfterIntroductionKiGraphSaga,
    api
  );
}

function* handleSearchDataTransitionAfterIntroductionKiGraphSaga() {
  yield takeLatest(
    DataTransitionAfterIntroductionKiGraphActionTypes.SEARCH_DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH,
    searchDataTransitionAfterIntroductionKiGraphSaga
  );
  yield takeLatest(
    DataTransitionAfterIntroductionKiGraphActionTypes.CHANGE_DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_FIELD,
    changeDataTransitionAfterIntroductionKiGraphFieldSaga
  );
  yield takeLatest(
    DataTransitionAfterIntroductionKiGraphActionTypes.SET_DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_FIELDS,
    setDataTransitionAfterIntroductionKiGraphFieldsSaga
  );
}

export function* dataTransitionAfterIntroductionKiGraphSaga(context: {
  api: Api;
}) {
  yield fork(
    handleFetchDataTransitionAfterIntroductionKiGraphSaga,
    context.api
  );
  yield fork(handleSearchDataTransitionAfterIntroductionKiGraphSaga);
}
